// jQuery-free AJAX / Deferred compatibility layer.
//
// Provides drop-in replacements for the small slice of the jQuery API the
// console app relied on ($.ajax, $.Deferred, $.when) so we can remove the
// global jQuery dependency without rewriting every call site's control flow.
//
//  - httpClient(options): XMLHttpRequest-based replacement for $.ajax. It is
//    XHR-based (not fetch) so the `xhr:` factory option used for upload
//    progress keeps working and the resolved object stays jqXHR-compatible.
//  - createDeferred(): replacement for $.Deferred().
//  - when(...values): replacement for $.when().
//
// The Deferred faithfully reproduces the jQuery semantics the code depends on:
// multi-argument .done/.fail/.always callbacks, and .then/.catch that return a
// chainable object (so `.then(...).fail(...)` keeps working).

function isThenable(value) {
    return (
        value &&
        (typeof value === 'object' || typeof value === 'function') &&
        typeof value.then === 'function'
    );
}

class Deferred {
    constructor() {
        this._state = 'pending';
        this._args = [];
        this._doneCbs = [];
        this._failCbs = [];
        this._alwaysCbs = [];
    }

    state() {
        return this._state;
    }

    resolve(...args) {
        if (this._state !== 'pending') {
            return this;
        }
        this._state = 'resolved';
        this._args = args;
        this._doneCbs.forEach((cb) => cb(...args));
        this._alwaysCbs.forEach((cb) => cb(...args));
        this._doneCbs = this._failCbs = this._alwaysCbs = [];
        return this;
    }

    reject(...args) {
        if (this._state !== 'pending') {
            return this;
        }
        this._state = 'rejected';
        this._args = args;
        this._failCbs.forEach((cb) => cb(...args));
        this._alwaysCbs.forEach((cb) => cb(...args));
        this._doneCbs = this._failCbs = this._alwaysCbs = [];
        return this;
    }

    done(cb) {
        if (typeof cb === 'function') {
            if (this._state === 'resolved') {
                cb(...this._args);
            } else if (this._state === 'pending') {
                this._doneCbs.push(cb);
            }
        }
        return this;
    }

    fail(cb) {
        if (typeof cb === 'function') {
            if (this._state === 'rejected') {
                cb(...this._args);
            } else if (this._state === 'pending') {
                this._failCbs.push(cb);
            }
        }
        return this;
    }

    always(cb) {
        if (typeof cb === 'function') {
            if (this._state !== 'pending') {
                cb(...this._args);
            } else {
                this._alwaysCbs.push(cb);
            }
        }
        return this;
    }

    then(onFulfilled, onRejected) {
        const next = new Deferred();

        const handle = (handler, args, fallback) => {
            if (typeof handler !== 'function') {
                fallback(...args);
                return;
            }
            let result;
            try {
                result = handler(...args);
            } catch (error) {
                next.reject(error);
                return;
            }
            if (isThenable(result)) {
                result.then(
                    (value) => next.resolve(value),
                    (error) => next.reject(error)
                );
            } else {
                next.resolve(result);
            }
        };

        this.done((...args) => handle(onFulfilled, args, (...a) => next.resolve(...a)));
        this.fail((...args) => handle(onRejected, args, (...a) => next.reject(...a)));

        return next;
    }

    catch(onRejected) {
        return this.then(undefined, onRejected);
    }

    promise() {
        return this;
    }
}

export function createDeferred() {
    return new Deferred();
}

export function when(...values) {
    const deferred = new Deferred();

    if (values.length === 0) {
        deferred.resolve();
        return deferred;
    }

    const results = new Array(values.length);
    let remaining = values.length;
    let rejected = false;
    const single = values.length === 1;

    const settleResolved = (index, args) => {
        results[index] = single ? args : args[0];
        remaining -= 1;
        if (remaining === 0 && !rejected) {
            if (single) {
                deferred.resolve(...results[index]);
            } else {
                deferred.resolve(...results);
            }
        }
    };

    values.forEach((value, index) => {
        const onResolve = (...args) => settleResolved(index, args);
        const onReject = (...errorArgs) => {
            if (!rejected) {
                rejected = true;
                deferred.reject(...errorArgs);
            }
        };

        if (value && typeof value.done === 'function' && typeof value.fail === 'function') {
            // jQuery/jqXHR-style deferreds notify synchronously via done/fail,
            // preserving the ordering the console relied on with $.when.
            value.done(onResolve).fail(onReject);
        } else if (isThenable(value)) {
            value.then(
                (resolved) => onResolve(resolved),
                (...errorArgs) => onReject(...errorArgs)
            );
        } else {
            onResolve(value);
        }
    });

    return deferred;
}

function getCookie(name) {
    if (!document.cookie) {
        return null;
    }
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i += 1) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === `${name}=`) {
            return decodeURIComponent(cookie.substring(name.length + 1));
        }
    }
    return null;
}

function csrfSafeMethod(method) {
    return /^(GET|HEAD|OPTIONS|TRACE)$/.test(method);
}

function sameOrigin(url) {
    if (!url) {
        return true;
    }
    const host = document.location.host;
    const protocol = document.location.protocol;
    const srOrigin = `//${host}`;
    const origin = protocol + srOrigin;
    return (
        url === origin ||
        url.slice(0, origin.length + 1) === `${origin}/` ||
        url === srOrigin ||
        url.slice(0, srOrigin.length + 1) === `${srOrigin}/` ||
        !/^(\/\/|http:|https:).*/.test(url)
    );
}

function serializeParams(data) {
    return Object.keys(data)
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
        .join('&');
}

function appendToUrl(url, extra) {
    if (!extra) {
        return url;
    }
    return url + (url.indexOf('?') === -1 ? '?' : '&') + extra;
}

const DEFAULT_CONTENT_TYPE = 'application/x-www-form-urlencoded; charset=UTF-8';

export function httpClient(options = {}) {
    const settings = {
        dataType: null,
        cache: true,
        contentType: DEFAULT_CONTENT_TYPE,
        processData: true,
        ...options
    };

    // jQuery resolves the verb as `options.method || options.type` - `method`
    // wins when both are given. Read from the raw `options` so no default can
    // mask an explicit `method:`, and so call sites that pass a non-verb
    // `type:` (e.g. the legacy `type: 'json'`) still fall back to their
    // `method:` instead of issuing a bogus `JSON` request.
    const verb = options.method || options.type || 'GET';
    const method = String(verb).toUpperCase();
    const deferred = new Deferred();
    const xhr = typeof settings.xhr === 'function' ? settings.xhr() : new window.XMLHttpRequest();

    let url = settings.url;
    let body = null;

    if (settings.data != null) {
        if (method === 'GET') {
            const query =
                typeof settings.data === 'string' ? settings.data : serializeParams(settings.data);
            url = appendToUrl(url, query);
        } else if (settings.processData === false || typeof settings.data === 'string') {
            body = settings.data;
        } else if (settings.contentType && settings.contentType.indexOf('json') !== -1) {
            body = JSON.stringify(settings.data);
        } else {
            body = serializeParams(settings.data);
        }
    }

    if (settings.cache === false && method === 'GET') {
        url = appendToUrl(url, `_=${Date.now()}`);
    }

    xhr.open(method, url, true);

    if (settings.contentType !== false && body != null && !(body instanceof window.FormData)) {
        xhr.setRequestHeader('Content-Type', settings.contentType);
    }
    if (settings.dataType === 'json') {
        xhr.setRequestHeader('Accept', 'application/json, text/javascript, */*; q=0.01');
    }
    // jQuery only sent this for same-origin requests (`!options.crossDomain`).
    // It is not a CORS-safelisted header, so adding it to calls such as JAWS,
    // station services or the YouTube API would force a preflight those
    // services may reject. A caller can still send it via `headers`.
    if (sameOrigin(settings.url)) {
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    }

    if (settings.headers) {
        Object.keys(settings.headers).forEach((header) => {
            xhr.setRequestHeader(header, settings.headers[header]);
        });
    }

    // Replicates the global CSRF handling that used to live in $.ajaxSetup.
    if (!csrfSafeMethod(method) && sameOrigin(settings.url)) {
        const csrftoken = getCookie('csrftoken');
        if (csrftoken) {
            xhr.setRequestHeader('X-CSRFToken', csrftoken);
        }
    }

    if (typeof settings.beforeSend === 'function') {
        settings.beforeSend(xhr, settings);
    }

    const parseResponse = () => {
        const contentType = xhr.getResponseHeader('Content-Type') || '';
        if (settings.dataType === 'json' || contentType.indexOf('json') !== -1) {
            try {
                return xhr.responseText ? JSON.parse(xhr.responseText) : null;
            } catch (error) {
                return null;
            }
        }
        return xhr.responseText;
    };

    xhr.onreadystatechange = () => {
        if (xhr.readyState !== 4) {
            return;
        }
        const statusText = xhr.statusText;
        const data = parseResponse();
        if (data && typeof data === 'object') {
            try {
                xhr.responseJSON = data;
            } catch (error) {
                // Some environments make XHR read-only; callers can still parse responseText.
            }
        }
        const succeeded = (xhr.status >= 200 && xhr.status < 300) || xhr.status === 304;
        if (succeeded) {
            if (typeof settings.success === 'function') {
                settings.success(data, statusText, xhr);
            }
            deferred.resolve(data, statusText, xhr);
        } else {
            const errorThrown = statusText || 'error';
            if (typeof settings.error === 'function') {
                settings.error(xhr, statusText, errorThrown);
            }
            deferred.reject(xhr, statusText, errorThrown);
        }
        if (typeof settings.complete === 'function') {
            settings.complete(xhr, statusText);
        }
    };

    xhr.onerror = () => {
        if (typeof settings.error === 'function') {
            settings.error(xhr, 'error', '');
        }
        deferred.reject(xhr, 'error', '');
        if (typeof settings.complete === 'function') {
            settings.complete(xhr, 'error');
        }
    };

    xhr.send(body);

    const promise = deferred.promise();
    promise.abort = () => xhr.abort();
    return promise;
}
